import type { Continuity } from '@dissidium/contracts';
import { validateLedger } from '../continuity/ledger.ts';
import { capabilityDefinition, findCapability } from '../items/catalogue.ts';
import { availableActions } from '../items/inventory.ts';
import type { CapabilityId } from '../items/types.ts';

export const PRACTICE_REQUIRED = 3;

export const PRACTICE_RULES: Readonly<Record<string, CapabilityId>> = {
  'shield-brace': 'brace-drill',
  cleave: 'blade-drill',
  'inspect-mechanism': 'fieldcraft',
  'prepare-treatment': 'rite-familiarity',
};

export interface LearningState {
  readonly uses: Readonly<Record<string, number>>;
}

export type LearningEvent =
  | { readonly type: 'action-practised'; readonly personId: string; readonly actionId: string; readonly uses: number }
  | { readonly type: 'capability-learned'; readonly personId: string; readonly capabilityId: CapabilityId };

/** Growth is classless: eligibility never consults the origin. */
export function canLearn(c: Continuity, personId: string, capabilityId: CapabilityId): boolean {
  const person = c.persons[personId];
  if (!person || person.lifecycle === 'dead') return false;
  if (!findCapability(capabilityId)) return false;
  return !person.current.abilities.includes(capabilityId);
}

export function learnCapability(input: Continuity, personId: string, capabilityId: CapabilityId): Continuity {
  const c = validateLedger(input);
  const person = c.persons[personId];
  if (!person) throw new Error('Unknown person');
  if (person.lifecycle === 'dead') throw new Error(`${person.current.name} is dead and cannot learn`);
  const capability = capabilityDefinition(capabilityId);
  const next = validateLedger(c);
  const target = next.persons[personId];
  if (!target) throw new Error('Unknown person');
  if (!target.current.abilities.includes(capability.id)) target.current.abilities.push(capability.id);
  return validateLedger(next);
}

export function recordActionUse(
  input: Continuity,
  state: LearningState,
  personId: string,
  actionId: string,
): { continuity: Continuity; learning: LearningState; events: readonly LearningEvent[] } {
  if (!availableActions(input, personId).includes(actionId)) throw new Error(`Action ${actionId} is not available to ${personId}`);
  const capabilityId = PRACTICE_RULES[actionId];
  if (!capabilityId || !canLearn(input, personId, capabilityId)) {
    return { continuity: input, learning: state, events: [] };
  }
  const key = `${personId}:${capabilityId}`;
  const uses = Math.min(PRACTICE_REQUIRED, (state.uses[key] ?? 0) + 1);
  const learning: LearningState = { uses: { ...state.uses, [key]: uses } };
  const events: LearningEvent[] = [{ type: 'action-practised', personId, actionId, uses }];
  if (uses < PRACTICE_REQUIRED) return { continuity: input, learning, events };
  events.push({ type: 'capability-learned', personId, capabilityId });
  return { continuity: learnCapability(input, personId, capabilityId), learning, events };
}
